import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    Inject,
    PLATFORM_ID,
    LOCALE_ID,
    ChangeDetectionStrategy
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ActivityReportChartItem } from '../../../pages/activity-reports/models/activity-reports.models';
import type { ApexOptions } from 'apexcharts';
@Component({
    selector: 'app-multiple-yaxis-chart',
    standalone: true,
    imports: [MatCardModule, TranslateModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './multiple-yaxis-chart.component.html'
})
export class MultipleYaxisChartComponent implements OnChanges, OnDestroy {
    @Input() items: ActivityReportChartItem[] = [];

    private isBrowser: boolean;
    private chart: any | null = null;
    private langSub?: Subscription;

    private readonly COLORS = {
        consumption: '#78007828',
        charge: '#be185d',
        discharge: '#4c1d95',
        price: '#2563eb',
        soc: '#10b981'
    };

    constructor(
        @Inject(PLATFORM_ID) platformId: any,
        @Inject(LOCALE_ID) private localeId: string,
        private translate: TranslateService
    ) {
        this.isBrowser = isPlatformBrowser(platformId);

        this.langSub = this.translate.onLangChange.subscribe(() => {
            if (!this.chart) return;
            const vm = this.buildViewModel(this.items);
            if (!vm) return;
            void this.safeUpdate(vm, false);
        });
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (!this.isBrowser) return;
        if (!changes['items']) return;

        const vm = this.buildViewModel(this.items);

        if (!vm) {
            await this.destroyChart();
            return;
        }

        if (!this.chart) {
            await this.createChart(vm);
            return;
        }

        await this.safeUpdate(vm, false);
    }

    ngOnDestroy(): void {
        this.langSub?.unsubscribe();
        void this.destroyChart();
    }

    private buildViewModel(items: ActivityReportChartItem[]) {
        if (!items || items.length === 0) return null;

        const lastHour = Math.max(...items.map(i => i.hour ?? 0));

        const sliced = items
            .filter(i => (i.hour ?? 0) <= lastHour)
            .sort((a, b) => (a.hour ?? 0) - (b.hour ?? 0));

        if (sliced.length === 0) return null;

        const categories = sliced.map(i => `${i.hour}:00`);
        const charge = sliced.map(i => this.toNumber(i.chargedKwh));
        const discharge = sliced.map(i => this.toNumber(i.dischargedKwh));
        const price = sliced.map(i => this.toNumber(i.electricityPrice));
        const consumption = sliced.map(i =>
            i.consumptionKwh != null ? this.toNumber(i.consumptionKwh) : null
        );
        const hasConsumption = consumption.some(v => v != null);
        const soc = sliced.map(i =>
            i.soc != null ? this.toNumber(i.soc) : null
        );
        const hasSoc = soc.some(v => v != null);

        const maxEnergyRaw = Math.max(
            0,
            ...charge,
            ...discharge,
            ...(hasConsumption ? consumption.map(v => v ?? 0) : [])
        );
        const maxEnergy = this.niceMax(maxEnergyRaw);

        return {
            categories,
            charge,
            discharge,
            price,
            consumption,
            hasConsumption,
            soc,
            hasSoc,
            maxEnergy
        };
    }

    private toNumber(v: any): number {
        const n = Number(v ?? 0);
        return Number.isFinite(n) ? n : 0;
    }

    private niceMax(v: number): number {
        if (!Number.isFinite(v) || v <= 0) return 1;
        const pow = Math.pow(10, Math.floor(Math.log10(v)));
        const n = v / pow;
        let m = 1;
        if (n <= 1) m = 1;
        else if (n <= 2) m = 2;
        else if (n <= 5) m = 5;
        else m = 10;
        return m * pow;
    }

    private get currentLocale(): string {
        return this.translate.currentLang || this.localeId || 'en';
    }

    private nf(maxFractionDigits: number) {
        return new Intl.NumberFormat(this.currentLocale, {
            maximumFractionDigits: maxFractionDigits,
            minimumFractionDigits: 0
        });
    }

    private fmtKwh = (v: any) => `${this.nf(2).format(Number(v ?? 0))} kWh`;
    private fmtPrice = (v: any) =>
        `${this.nf(3).format(Number(v ?? 0))} DKK/kWh`;
    private fmtPercent = (v: any) => `${this.nf(1).format(Number(v ?? 0))}%`;

    private t(key: string): string {
        return this.translate.instant(key);
    }

    private buildOptions(vm: {
        categories: string[];
        charge: number[];
        discharge: number[];
        price: number[];
        consumption: (number | null)[];
        hasConsumption: boolean;
        soc: (number | null)[];
        hasSoc: boolean;
        maxEnergy: number;
    }): ApexOptions {
        const chargeName = this.t('activity.charge');
        const dischargeName = this.t('activity.discharge');
        const priceName = this.t('activity.price');
        const consumptionName = this.t('activity.consumption');
        const socName = this.t('Battery Soc(%)');

        const series: any[] = [];
        let socSeriesIndex: number | null = null;
        if (vm.hasConsumption) {
            series.push({
                name: consumptionName,
                type: 'area',
                data: vm.consumption
            });
        }
        series.push(
            { name: chargeName, type: 'column', data: vm.charge },
            { name: dischargeName, type: 'column', data: vm.discharge }
        );
        if (vm.hasSoc) {
            socSeriesIndex = series.length;
            series.push({ name: socName, type: 'scatter', data: vm.soc });
        }
        series.push({ name: priceName, type: 'line', data: vm.price });

        const colors: string[] = [];
        if (vm.hasConsumption) colors.push(this.COLORS.consumption);
        colors.push(this.COLORS.charge, this.COLORS.discharge);
        if (vm.hasSoc) colors.push(this.COLORS.soc);
        colors.push(this.COLORS.price);

        const strokeWidths: number[] = [];
        if (vm.hasConsumption) strokeWidths.push(1);
        strokeWidths.push(1, 1);
        if (vm.hasSoc) strokeWidths.push(0);
        strokeWidths.push(3);

        const energyAxis = {
            min: 0,
            max: vm.maxEnergy,
            title: { text: 'Value (DKK/KWH)' },
            labels: {
                formatter: (val: any) => this.nf(2).format(Number(val ?? 0))
            },
            decimalsInFloat: 2
        };

        const yaxis: any[] = [energyAxis];

        if (vm.hasConsumption) {
            yaxis.push({
                min: 0,
                max: vm.maxEnergy,
                show: false,
                labels: { show: false },
                axisBorder: { show: false },
                axisTicks: { show: false }
            });
        }
        yaxis.push({
            min: 0,
            max: vm.maxEnergy,
            show: false,
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false }
        });

        if (vm.hasSoc) {
            yaxis.push({
                min: 0,
                max: 100,
                opposite: true,
                title: { text: 'SOC (%)' },
                labels: {
                    formatter: (val: any) => this.nf(0).format(Number(val ?? 0))
                },
                decimalsInFloat: 0
            });
        }

        yaxis.push({
            opposite: true,
            show: false,
            title: { text: '' },
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false },
            decimalsInFloat: 3
        });

        const markerSizes = new Array(series.length).fill(0);

        const priceSeriesIndex = series.length - 1;
        markerSizes[priceSeriesIndex] = 4;

        if (socSeriesIndex != null) {
            markerSizes[socSeriesIndex] = 0;
        }

        const dataLabels: any = { enabled: false };
        if (socSeriesIndex != null) {
            dataLabels.enabled = true;
            dataLabels.enabledOnSeries = [socSeriesIndex];
            dataLabels.textAnchor = 'middle';
            dataLabels.offsetY = 0;
            dataLabels.style = { fontSize: '20px' };
            dataLabels.background = { enabled: false };
            dataLabels.formatter = (val: any) => (val == null ? '' : '🔋');
        }

        const tooltipY: any[] = [];
        if (vm.hasConsumption) {
            tooltipY.push({
                title: { formatter: (s: string) => `${s} : ` },
                formatter: (val: any) => this.fmtKwh(val)
            });
        }
        tooltipY.push(
            {
                title: { formatter: (s: string) => `${s} : ` },
                formatter: (val: any) => this.fmtKwh(val)
            },
            {
                title: { formatter: (s: string) => `${s} : ` },
                formatter: (val: any) => this.fmtKwh(val)
            }
        );
        if (vm.hasSoc) {
            tooltipY.push({
                title: { formatter: (s: string) => `${s} : ` },
                formatter: (val: any) => this.fmtPercent(val)
            });
        }
        tooltipY.push({
            title: { formatter: (s: string) => `${s} : ` },
            formatter: (val: any) => this.fmtPrice(val)
        });

        return {
            colors,
            series,
            chart: {
                height: 350,
                type: 'line',
                stacked: false,
                toolbar: { show: false },
                zoom: { enabled: false }
            },
            dataLabels,
            stroke: { width: strokeWidths },
            plotOptions: { bar: { columnWidth: '45%' } },
            markers: {
                size: markerSizes,
                hover: { sizeOffset: 2 }
            },
            xaxis: {
                categories: vm.categories,
                axisBorder: { show: false },
                axisTicks: { show: true }
            },
            yaxis,
            tooltip: {
                shared: true,
                intersect: false,
                y: tooltipY
            },
            legend: {
                horizontalAlign: 'left',
                fontSize: '14px'
            },
            grid: { strokeDashArray: 5 }
        };
    }

    private async createChart(vm: any): Promise<void> {
        const ApexCharts = (await import('apexcharts')).default;
        const options = this.buildOptions(vm);

        const el =
            document.querySelector<HTMLElement>('#multiple_yAxis_chart');

        if (!el) {
            console.error('Multiple Y-axis chart element not found');
            return;
        }

        this.chart = new ApexCharts(el, options);

        await this.chart.render();
    }

    private async safeUpdate(vm: any, forceRecreate: boolean) {
        if (!this.chart || forceRecreate) {
            await this.destroyChart();
            await this.createChart(vm);
            return;
        }

        try {
            const opts = this.buildOptions(vm);
            await this.chart.updateOptions(opts, true, true, false);
        } catch {
            await this.destroyChart();
            await this.createChart(vm);
        }
    }

    private async destroyChart() {
        if (this.chart) {
            try {
                await this.chart.destroy();
            } catch { }
            this.chart = null;
        }
    }
}
